// Inspecteur · Verwerking detail — the Art. 30 record's fields + its canonical-Activity LINEAGE
// (Verordening › §Section › code › Activity) with an "instance of canonical Activity" / "free-standing"
// badge (FRONTEND-BLUEPRINT §2.3). Reads getVerwerking + getVerwerkingLineage (RLS-scoped) via
// @sympllex/api-client + TanStack Query. Supersedes the legacy routes/verwerking-detail.tsx.
import { useState, type ReactNode } from "react";
import { useParams, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@sympllex/api-client";
import type { VerwerkingLineage } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { Can } from "../../lib/rbac";
import { useI18n } from "../../lib/i18n";
import { enumLabel, humanizeEnum } from "../../lib/enum-labels";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const errText = (e: unknown) => (e instanceof ApiError ? `Server ${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message);

// Mirror of @sympllex/domain.lineageBreadcrumb — kept inline so the browser bundle does not pull in the
// domain package's DB (drizzle) runtime. "Verordening › §Section › code › Activity".
function lineageBreadcrumb(l: Pick<VerwerkingLineage, "regulationTitle" | "sectionCode" | "activityCode" | "activityName">): string {
  return [l.regulationTitle, l.sectionCode ? `§${l.sectionCode}` : null, l.activityCode, l.activityName]
    .filter(Boolean)
    .join(" › ");
}

export function VerwerkingDetailRoute() {
  const { id } = useParams({ from: "/verwerkingen/$id" });
  const { t } = useI18n();
  const verwerking = useQuery({ queryKey: ["verwerking", id], queryFn: () => api.getVerwerking(id) });
  const lineageQ = useQuery({ queryKey: ["verwerking-lineage", id], queryFn: () => api.getVerwerkingLineage(id) });

  const row = (verwerking.data ?? null) as Record<string, unknown> | null;
  const lineage = (lineageQ.data ?? null) as VerwerkingLineage | null;

  return (
    <section>
      <Link to="/verwerkingen" className="mb-4 inline-block text-sm text-primary hover:underline">
        ← {t("verw.detail.back")}
      </Link>
      <PageHeader title={String(row?.name ?? t("verw.detail.title"))} description={t("verw.detail.title")} />

      {verwerking.error && <p className="text-sm text-destructive">{t("verw.detail.loadFailed")} {errText(verwerking.error)}</p>}

      <div className="grid gap-4">
        <LineageCard lineage={lineage} loading={lineageQ.isLoading} />
        <FieldsCard row={row} loading={verwerking.isLoading} />
        <DoelenCard verwerkingId={id} />
        <FilingSystemsCard verwerkingId={id} />
        <HistoryCard verwerkingId={id} />
      </div>
    </section>
  );
}

// Doelen (purposes) — a verwerking has many; each is the per-purpose home of the Art.30 detail (Phase A,
// MASTER-PICTURE §1.2). List + create + open into the doel-detail page (categories/data-subjects/legal
// basis/recipients per purpose). Mirrors the legacy verwerking "Doelen" tab.
function DoelenCard({ verwerkingId }: { verwerkingId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ["verwerking-child", verwerkingId, "doelen"];
  const doelen = useQuery({ queryKey: key, queryFn: () => api.listVerwerkingChild(verwerkingId, "doelen") });
  const [name, setName] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: () => api.addVerwerkingChild(verwerkingId, "doelen", { name: name.trim(), description: "" }),
    onSuccess: () => { setName(""); invalidate(); },
  });
  const remove = useMutation({ mutationFn: (cid: string) => api.removeVerwerkingChild(verwerkingId, "doelen", cid), onSuccess: invalidate });
  const rows = (doelen.data ?? []) as Array<Record<string, unknown>>;

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("verw.detail.doelen.title")}</CardTitle></CardHeader>
      <CardContent>
        {doelen.isLoading ? <Skeleton className="h-5 w-2/3" /> : (
          <>
            {doelen.error && <p className="text-sm text-destructive">{t("crud.failed")} {errText(doelen.error)}</p>}
            {!doelen.error && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("verw.detail.doelen.empty")}</p>}
            <ul className="m-0 list-none p-0">
              {rows.map((r) => (
                <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                  <Link to="/verwerkingen/$id/doelen/$doelId" params={{ id: verwerkingId, doelId: String(r.id) }}
                    className="inline-flex min-w-0 items-center gap-2 text-primary hover:underline">
                    <span className="truncate font-medium">{String(r.name ?? r.description ?? "—")}</span>
                    <Badge variant="secondary">{enumLabel(t, "verwerkingStatus", r.status ?? "active")}</Badge>
                  </Link>
                  <Can perm="verwerking.update">
                    <Button variant="ghost" size="xs" className="text-destructive" disabled={remove.isPending} onClick={() => remove.mutate(String(r.id))}>
                      {t("verw.detail.doelen.remove")}
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
            <Can perm="verwerking.write">
              <div className="mt-3 flex items-center gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("verw.detail.doelen.placeholder")} className="max-w-sm" />
                <Button size="sm" disabled={add.isPending || !name.trim()} onClick={() => add.mutate()}>
                  {add.isPending ? t("crud.creating") : t("verw.detail.doelen.add")}
                </Button>
              </div>
              {add.isError && <p className="mt-2 text-sm text-destructive">{errText(add.error)}</p>}
            </Can>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Bestandssystemen junction panel (PLAN-W27 B; blueprint §4 "M:N — filing systems a Verwerking
// stores data in"). List/add/remove over the /verwerkingen/:id/bestandssystemen child routes
// (api.listVerwerkingChild / addVerwerkingChild / removeVerwerkingChild), picking from the
// tenant's Bestandssystemen register. Affordances gated verwerking.write / verwerking.update
// (the worker enforces server-side), mirroring the other Verwerking junction panels.
function FilingSystemsCard({ verwerkingId }: { verwerkingId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const key = ["verwerking-child", verwerkingId, "bestandssystemen"];
  const linked = useQuery({ queryKey: key, queryFn: () => api.listVerwerkingChild(verwerkingId, "bestandssystemen") });
  const register = useQuery({ queryKey: ["bestandssystemen"], queryFn: () => api.listBestandssystemen() });
  const [sel, setSel] = useState("");
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const add = useMutation({
    mutationFn: () => api.addVerwerkingChild(verwerkingId, "bestandssystemen", { bestandssysteemId: sel }),
    onSuccess: () => { setSel(""); invalidate(); },
  });
  const remove = useMutation({
    mutationFn: (childId: string) => api.removeVerwerkingChild(verwerkingId, "bestandssystemen", childId),
    onSuccess: invalidate,
  });

  const rows = (linked.data ?? []) as Array<Record<string, unknown>>;
  const options = (register.data ?? []) as Array<Record<string, unknown>>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("verw.detail.fs.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {linked.isLoading ? (
          <Skeleton className="h-5 w-2/3" />
        ) : (
          <>
            {linked.error && <p className="text-sm text-destructive">{t("crud.failed")} {errText(linked.error)}</p>}
            {!linked.error && rows.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("verw.detail.fs.empty")}</p>
            )}
            <ul className="m-0 list-none p-0">
              {rows.map((r) => (
                <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium">{String(r.name ?? "—")}</span>
                    {r.category ? <Badge variant="secondary">{humanizeEnum(r.category)}</Badge> : null}
                  </span>
                  <Can perm="verwerking.update">
                    <Button variant="ghost" size="xs" className="text-destructive" disabled={remove.isPending} onClick={() => remove.mutate(String(r.id))}>
                      {t("verw.detail.fs.remove")}
                    </Button>
                  </Can>
                </li>
              ))}
            </ul>
            <Can perm="verwerking.write">
              {options.length === 0 && rows.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">{t("verw.detail.fs.noOptions")}</p>
              ) : (
                <div className="mt-3 flex items-center gap-2">
                  <select
                    value={sel}
                    onChange={(e) => setSel(e.target.value)}
                    aria-label={t("verw.detail.fs.title")}
                    className="h-8 w-full max-w-sm rounded-2xl border border-transparent bg-input/50 px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30"
                  >
                    <option value="">{t("crud.select")}</option>
                    {options.map((o) => (
                      <option key={String(o.id)} value={String(o.id)}>{String(o.name ?? o.id)}</option>
                    ))}
                  </select>
                  <Button size="sm" disabled={add.isPending || !sel} onClick={() => add.mutate()}>
                    {add.isPending ? t("crud.creating") : t("verw.detail.fs.add")}
                  </Button>
                </div>
              )}
              {add.isError && <p className="mt-2 text-sm text-destructive">{errText(add.error)}</p>}
            </Can>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// Wijzigingsgeschiedenis — the RoPA snapshot trail (design-vs-build Theme 4). Read-only audit of saved
// versions via GET /verwerkingen/:id/history (listVerwerkingSnapshots), which was built but never surfaced.
function HistoryCard({ verwerkingId }: { verwerkingId: string }) {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["verwerking-history", verwerkingId], queryFn: () => api.listVerwerkingHistory(verwerkingId) });
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t("verw.detail.history.title")}</CardTitle></CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-5 w-2/3" /> : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("verw.detail.history.empty")}</p>
        ) : (
          <ul className="m-0 list-none p-0">
            {rows.map((r) => (
              <li key={String(r.id)} className="flex items-center justify-between gap-3 border-b py-2 text-sm last:border-b-0">
                <span className="font-medium">{String(r.snapshotReason ?? "—")}</span>
                <span className="text-xs text-muted-foreground">{r.takenAt ? new Date(String(r.takenAt)).toLocaleString() : "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function LineageCard({ lineage, loading }: { lineage: VerwerkingLineage | null; loading: boolean }) {
  const { t } = useI18n();
  const isInstance = lineage?.isInstance === true;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-base">{t("verw.detail.lineage")}</CardTitle>
        {!loading && (
          <Badge variant={isInstance ? "default" : "outline"}>
            {isInstance ? t("verw.detail.instance") : t("verw.detail.freeStanding")}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-5 w-2/3" />
        ) : isInstance && lineage ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium">{lineageBreadcrumb(lineage) || "—"}</p>
            {lineage.activityType && (
              <p className="text-xs text-muted-foreground">
                {t("verw.detail.activityType")}: <span className="font-medium">{lineage.activityType}</span>
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("verw.detail.noLineage")}</p>
        )}
      </CardContent>
    </Card>
  );
}

// Art.30 record fields read straight off the verwerking row. A focused, read-oriented summary; full
// junction editing (purposes / recipients / retention / categories) lives in the round-2 detail module.
function FieldsCard({ row, loading }: { row: Record<string, unknown> | null; loading: boolean }) {
  const { t } = useI18n();
  const fields: { label: string; value: ReactNode }[] = row
    ? [
        { label: t("verw.col.status"), value: <Badge variant="secondary">{enumLabel(t, "verwerkingStatus", row.status)}</Badge> },
        { label: t("verw.field.role"), value: enumLabel(t, "actingOrgRole", row.actingOrgRole ?? row.acting_org_role) },
        { label: "Art. 22", value: (row.involvesArt22AutomatedDecision ?? row.involves_art22_automated_decision) ? t("verw.yes") : t("verw.no") },
        { label: "Art. 14", value: String(row.art14Source ?? row.art14_source ?? "—") },
      ]
    : [];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("verw.detail.fields")}</CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-5 w-1/2" />)}</div>
        ) : (
          <dl className="grid gap-3 sm:grid-cols-2">
            {row?.description ? (
              <div className="sm:col-span-2">
                <dt className="text-xs text-muted-foreground">{t("verw.field.description")}</dt>
                <dd className="text-sm">{String(row.description)}</dd>
              </div>
            ) : null}
            {fields.map((f) => (
              <div key={f.label}>
                <dt className="text-xs text-muted-foreground">{f.label}</dt>
                <dd className="text-sm">{f.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </CardContent>
    </Card>
  );
}
